import { useContext, useMemo } from 'react';
import { ID } from 'types';
import { LineItemsContext } from '@providers/LineItemsProvider';
import { useLotIdErrors } from '@features/buyer-line-items-pagination';

/**
 * Hook that extracts only the data specific to a single lot.
 * This prevents unnecessary re-renders when other lots change.
 * ONLY returns lot-specific data to avoid global state re-renders.
 */
export const useLotData = (lotId: ID) => {
  const { state, dispatch } = useContext(LineItemsContext);
  const { lots, lotsErrors, changes, event } = state;
  const paginationEnabled = Boolean(event?.paginationEnabled);

  // Extract lot-specific data first
  const lot = lots?.find(l => l.id === lotId);
  const lotErrors = lotsErrors?.[lotId];
  const lotIdErrors = useLotIdErrors(lotId);
  const lotChanges = changes?.[lotId];

  // Create stable references for lot-specific data ONLY - only depend on THIS lot's data
  // NOTE: This will still recalculate when lot object reference changes, even if content is same.
  // This is a known limitation - for deeper optimization, consider implementing deep comparison
  // or restructuring the context to provide stable references.
  const lotData = useMemo(() => {
    return {
      dispatch,
      hasLotChanges: Object.keys(lotChanges || {}).length > 0,
      hasLotErrors: Object.keys((paginationEnabled ? lotIdErrors : lotErrors) || {}).length > 0,
      lot,
      lotChanges: lotChanges || {},
      lotErrors: lotErrors || {},
      lotIdErrors,
      paginationEnabled,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lot, lotErrors, lotIdErrors, lotChanges, paginationEnabled]); // Only THIS lot's data, dispatch is stable from useReducer

  return lotData;
};

/**
 * Hook for accessing shared/global state from LineItemsProvider.
 * Use this for state that all lots need (event, dispatch, etc.)
 */
export const useSharedLineItemsData = () => {
  const { state, dispatch, hasChanges, request } = useContext(LineItemsContext);

  // Destructure state properties once for DRY
  const {
    event,
    isInProgress,
    isLoading,
    refreshValidation,
    shouldRerenderLineItemData
  } = state;

  return useMemo(() => ({
    dispatch,
    event,
    hasChanges,
    isInProgress,
    isLoading,
    refreshValidation,
    request,
    shouldRerenderLineItemData,
  }), [
    dispatch,
    event,
    hasChanges,
    isInProgress,
    isLoading,
    refreshValidation,
    request,
    shouldRerenderLineItemData,
  ]);
};
